import { createApi } from "@reduxjs/toolkit/query/react";
import { customFetchBaseQuery } from "./customFetchBaseQuery";

/**
 * The search, encoded: a raw "#302" made the browser drop the unit as a URL
 * fragment, and "&" split the search (Oct 20 call).
 */
export const autoCompleteUrl = (query: unknown) =>
  `/api/property/auto-complete?search=${encodeURIComponent(String(query ?? ""))}`;

export const autoCompleteApiQuery = createApi({
  reducerPath: "autoComplete",
  baseQuery: customFetchBaseQuery,
  keepUnusedDataFor: 300, // Default: cache for 5 minutes
  refetchOnFocus: false, // Don't refetch when window regains focus
  refetchOnMountOrArgChange: true, // Refetch when args change (important for filters)
  endpoints: (builder) => {
    return {
      getAutoCompleteResult: builder.query({
        query: (query) => autoCompleteUrl(query),
        keepUnusedDataFor: 0, // Autocomplete should always be fresh
      }),
      getProprtyStatus: builder.query({
        query: (query) => `/api/property/property-id/${query}/property`,
        keepUnusedDataFor: 60, // Cache property status for 1 minute
      }),
      getPropertyCollections: builder.query({
        query: (query) => `/api/property/${query}`,
        // Transform response to flatten structure for easier access
        transformResponse: (response: any) => {
          return {
            results: response?.data?.results || [],
            totalCount: response?.data?.results?.length || 0,
          };
        },
      }),
      getPropertyClimateDetails: builder.query({
        query: (query) => `/api/property/${query}`,
        keepUnusedDataFor: 300, // Cache climate details for 5 minutes
      }),
      getPropertyNeighbourhoodDetails: builder.query({
        query: ({
          latitude,
          longitude,
          propertyId,
        }: {
          latitude: number;
          longitude: number;
          propertyId: string;
        }) =>
          `/api/property/neighbourhoodActivities?latitude=${latitude}&longitude=${longitude}&propertyId=${propertyId}`,
        keepUnusedDataFor: 300, // Cache neighbourhood details for 5 minutes
      }),
      getPropertySchools: builder.query({
        query: ({
          latitude,
          longitude,
          propertyId,
        }: {
          latitude: number;
          longitude: number;
          propertyId: string;
        }) =>
          `/api/property/schools?latitude=${latitude}&longitude=${longitude}&propertyId=${propertyId}`,
        transformResponse: (response: any) => response?.data || { schools: [] },
        keepUnusedDataFor: 300, // Cache schools for 5 minutes
      }),
      // Local hiking trails (October 2026, Property Details). Signed-in only.
      getPropertyTrails: builder.query({
        query: ({
          latitude,
          longitude,
          propertyId,
        }: {
          latitude: number;
          longitude: number;
          propertyId: string;
        }) =>
          `/api/property/trails?latitude=${latitude}&longitude=${longitude}&propertyId=${encodeURIComponent(propertyId)}`,
        transformResponse: (response: any) => ({
          trails: response?.data?.trails || [],
          unavailable: response?.data?.unavailable === true,
        }),
        keepUnusedDataFor: 300,
      }),
      getRecentlyAddedProperty: builder.query({
        query: () => `/api/property/recently-added`,
        keepUnusedDataFor: 300, // Cache for 5 minutes
      }),
      getAllStateList: builder.query({
        query: () => `/api/state/list`,
        keepUnusedDataFor: 3600, // Cache for 1 hour (rarely changes)
      }),
      getAllCityList: builder.query({
        query: () => `/api/city/list`,
        keepUnusedDataFor: 3600, // Cache for 1 hour (rarely changes)
      }),
      getAllLocalNewsList: builder.query({
        query: (query) => `/api/property/news/${query}`,
        keepUnusedDataFor: 300, // Cache news for 5 minutes
      }),
      getPropertyBasedOnLocation: builder.query({
        query: ({ lat, long }) =>
          `/api/property/lat-long?lat=${lat}&long=${long}`,
        keepUnusedDataFor: 300, // Cache location-based results for 5 minutes
      }),
      getPartnerLists: builder.query({
        query: () => `/api/partner/list`,
        keepUnusedDataFor: 3600, // Cache for 1 hour (rarely changes)
      }),
      getLocalServicesList: builder.query({
        query: () => `/api/local-service/list`,
        keepUnusedDataFor: 3600, // Cache for 1 hour (rarely changes)
      }),
      // Fetch property IDs only (lightweight endpoint for lazy loading)
      getPropertySearchIds: builder.query({
        query: ({
          filterType,
          filterValue,
          resultIndex = 0,
          minBeds,
          minBaths,
          minYearBuilt,
          maxYearBuilt,
          sortBy,
          hasAttic,
          hasBasement,
          hasDeck,
          hasBalcony,
          hasGarage,
          hasPatio,
          hasPool,
          hasAirConditioning,
        }: {
          filterType: string;
          filterValue: string;
          resultIndex?: number;
          minBeds?: string;
          minBaths?: string;
          minYearBuilt?: number;
          maxYearBuilt?: number;
          sortBy?: string;
          hasAttic?: boolean;
          hasBasement?: boolean;
          hasDeck?: boolean;
          hasBalcony?: boolean;
          hasGarage?: boolean;
          hasPatio?: boolean;
          hasPool?: boolean;
          hasAirConditioning?: boolean;
        }) => {
          const params = new URLSearchParams();
          params.append("resultIndex", resultIndex.toString());
          if (minBeds) params.append("minBeds", minBeds);
          if (minBaths) params.append("minBaths", minBaths);
          if (minYearBuilt) params.append("minYearBuilt", minYearBuilt.toString());
          if (maxYearBuilt) params.append("maxYearBuilt", maxYearBuilt.toString());
          if (sortBy) params.append("sortBy", sortBy);
          // Amenity filters
          if (hasAttic) params.append("attic", "true");
          if (hasBasement) params.append("basement", "true");
          if (hasDeck) params.append("deck", "true");
          if (hasBalcony) params.append("feature_balcony", "true");
          if (hasGarage) params.append("garage", "true");
          if (hasPatio) params.append("patio", "true");
          if (hasPool) params.append("pool", "true");
          if (hasAirConditioning) params.append("air_conditioning_available", "true");

          return `/api/property/search-ids/${filterType}/${filterValue}?${params.toString()}`;
        },
        transformResponse: (response: any) => ({
          propertyIds: response?.data?.propertyIds || [],
          totalCount: response?.data?.totalCount || 0,
          resultIndex: response?.data?.resultIndex || 0,
          hasMore: response?.data?.hasMore ?? false,
        }),
        keepUnusedDataFor: 0, // Never cache - always fetch fresh results for filtering
      }),
      // Fetch single property by ID (uses existing backend with caching)
      getPropertyById: builder.query({
        query: (propertyId: string) =>
          `/api/property/property-id/${propertyId}/property`,
        transformResponse: (response: any) => response?.data || null,
        keepUnusedDataFor: 300, // Cache for 5 minutes
      }),
      // Weather/Climate data endpoint - cached at backend for 24 hours
      getWeatherClimateData: builder.query({
        query: ({ city, state }: { city: string; state: string }) =>
          `/api/weather/climate?city=${encodeURIComponent(city)}&state=${encodeURIComponent(state)}`,
        transformResponse: (response: any) => response?.data || null,
        keepUnusedDataFor: 3600, // Cache for 1 hour on frontend (backend has 24hr TTL)
      }),
    };
  },
});

export const {
  useGetAutoCompleteResultQuery,
  useLazyGetAutoCompleteResultQuery,
  useGetProprtyStatusQuery,
  useGetPropertyCollectionsQuery,
  useLazyGetPropertyCollectionsQuery,
  useGetPropertyNeighbourhoodDetailsQuery,
  useLazyGetPropertyNeighbourhoodDetailsQuery,
  useGetPropertySchoolsQuery,
  useLazyGetPropertySchoolsQuery,
  useGetPropertyTrailsQuery,
  useGetAllStateListQuery,
  useLazyGetPropertyBasedOnLocationQuery,
  useGetPropertyBasedOnLocationQuery,
  useGetPartnerListsQuery,
  useLazyGetPartnerListsQuery,
  useGetRecentlyAddedPropertyQuery,
  useLazyGetRecentlyAddedPropertyQuery,
  useGetLocalServicesListQuery,
  useGetAllCityListQuery,
  useLazyGetAllCityListQuery,
  useGetAllLocalNewsListQuery,
  // New hooks for lazy loading
  useGetPropertySearchIdsQuery,
  useLazyGetPropertySearchIdsQuery,
  useGetPropertyByIdQuery,
  useLazyGetPropertyByIdQuery,
  // Weather/Climate data hooks
  useGetWeatherClimateDataQuery,
  useLazyGetWeatherClimateDataQuery,
} = autoCompleteApiQuery;
